import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';

// Types
interface Threshold {
  FieldID: number;
  FieldName: string;
  SoftLower: number | null;
  HardLower: number | null;
  SoftUpper: number | null;
  HardUpper: number | null;
}

interface FieldData {
  FieldID: number;
  FieldName: string;
  L1: string;
  L2: string;
  U1: string;
  U2: string;
  Value: string;
  Note: string;
  MonitorDataID: number | null;
  HasChanges: boolean;
  UserName?: string;
  Timestamp?: string;
}

// Field IDs from Access constants
const FIELD_ID_AUM_FUND_USD = 20;  // AUM Fund ($mm) USD
const FIELD_ID_AUM_FUND_EUR = 79;  // AUM Fund ($mm) EUR
const FIELD_ID_AUM_FUND_CHG = 2;   // AUM Fund Change (%)
const FIELD_ID_AUM_STRAT_USD = 21; // AUM Strategy ($mm) USD (if exists)
const FIELD_ID_AUM_STRAT_EUR = 80; // AUM Strategy ($mm) EUR (if exists)
const FIELD_ID_AUM_STRAT_CHG = 3;  // AUM Strategy Change (%) (if exists)
const FIELD_ID_MONTHLY_ROR = 6;    // Monthly ROR (%)

const DataEntryForm: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { funds, monitorDates } = useHedgeFundData();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [selectedFund, setSelectedFund] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [investedOnly, setInvestedOnly] = useState(true);
  
  // Helper to format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Data state
  const [fieldData, setFieldData] = useState<FieldData[]>([]);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);

  // Filter funds based on invested checkbox
  const displayedFunds = investedOnly 
    ? funds.filter(f => f.Invested) 
    : funds;

  // Load thresholds when fund changes
  useEffect(() => {
    if (selectedFund) {
      loadThresholds();
      // Set default date to most recent after fund selection
      if (monitorDates.length > 0) {
        setSelectedDate(new Date(monitorDates[0].MonitorDate));
      }
    } else {
      setFieldData([]);
      setThresholds([]);
      setSelectedDate(null);
    }
  }, [selectedFund]);

  // Reload thresholds when date changes (to get date-specific thresholds)
  useEffect(() => {
    if (selectedFund && selectedDate) {
      // Reset the load ref so data will load for the new date
      lastLoadedRef.current = '';
      loadThresholds();
    }
  }, [selectedDate]);

  // Track if data is being loaded to prevent auto-calculation during load
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Track the last loaded fund/date combo to prevent duplicate loads
  const lastLoadedRef = React.useRef<string>('');
  
  // Load monitor data when both fund and date are selected (only once per fund/date change)
  useEffect(() => {
    if (selectedFund && selectedDate && fieldData.length > 0) {
      const loadKey = `${selectedFund}-${formatDateForAPI(selectedDate)}`;
      
      // Skip if already loading this exact combo
      if (lastLoadedRef.current === loadKey) {
        return;
      }
      
      lastLoadedRef.current = loadKey;
      setIsLoadingData(true);
      
      loadMonitorData().finally(() => {
        setTimeout(() => setIsLoadingData(false), 200);
      });
    }
  }, [selectedFund, selectedDate, fieldData.length]);

  const loadThresholds = async () => {
    if (!selectedFund) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      let url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-thresholds?backstop_id=${selectedFund}`;
      
      if (selectedDate) {
        url += `&threshold_date=${formatDateForAPI(selectedDate)}`;
      } else {
        url += `&newest=1`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load thresholds');

      const data = await response.json();
      setThresholds(data);
      
      setFieldData(prev => {
        if (prev.length === 0 || prev.length !== data.length) {
          return data.map((threshold: Threshold) => ({
            FieldID: threshold.FieldID,
            FieldName: threshold.FieldName,
            L1: threshold.SoftLower !== null ? threshold.SoftLower.toString() : '',
            L2: threshold.HardLower !== null ? threshold.HardLower.toString() : '',
            U1: threshold.SoftUpper !== null ? threshold.SoftUpper.toString() : '',
            U2: threshold.HardUpper !== null ? threshold.HardUpper.toString() : '',
            Value: '',
            Note: '',
            MonitorDataID: null,
            HasChanges: false
          }));
        }
        
        return data.map((threshold: Threshold) => {
          const existing = prev.find(f => f.FieldID === threshold.FieldID);
          return {
            FieldID: threshold.FieldID,
            FieldName: threshold.FieldName,
            L1: threshold.SoftLower !== null ? threshold.SoftLower.toString() : '',
            L2: threshold.HardLower !== null ? threshold.HardLower.toString() : '',
            U1: threshold.SoftUpper !== null ? threshold.SoftUpper.toString() : '',
            U2: threshold.HardUpper !== null ? threshold.HardUpper.toString() : '',
            Value: existing?.Value || '',
            Note: existing?.Note || '',
            MonitorDataID: existing?.MonitorDataID || null,
            HasChanges: existing?.HasChanges || false,
            UserName: existing?.UserName,
            Timestamp: existing?.Timestamp
          };
        });
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load thresholds');
      setThresholds([]);
      setFieldData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonitorData = async (): Promise<void> => {
    if (!selectedFund || !selectedDate) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const dateStr = formatDateForAPI(selectedDate);
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data?backstop_id=${selectedFund}&start_date=${dateStr}&end_date=${dateStr}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load monitor data');

      const data = await response.json();
      
      setFieldData(prev => {
        const updated = prev.map(field => {
          const existingData = data.find((d: any) => d.FieldID === field.FieldID);
          if (existingData) {
            return {
              ...field,
              Value: existingData.FieldValue !== null ? existingData.FieldValue.toString() : '',
              Note: existingData.Note || '',
              MonitorDataID: existingData.MonitorDataID,
              HasChanges: false,
              UserName: existingData.UserName,
              Timestamp: existingData.Timestamp
            };
          }
          return { ...field, Value: '', Note: '', MonitorDataID: null, HasChanges: false, UserName: undefined, Timestamp: undefined };
        });
        return updated;
      });
      
    } catch (err) {
      console.error('Error loading monitor data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load monitor data');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (fieldId: number, value: string) => {
    setFieldData(prev => prev.map(field => 
      field.FieldID === fieldId 
        ? { ...field, Value: value, HasChanges: true }
        : field
    ));
  };

  const handleNoteChange = (fieldId: number, note: string) => {
    setFieldData(prev => prev.map(field => 
      field.FieldID === fieldId 
        ? { ...field, Note: note, HasChanges: true }
        : field
    ));
  };

  const handleSave = async () => {
    if (!selectedFund || !selectedDate) {
      setError('Please select a fund and date');
      return;
    }

    const changedFields = fieldData.filter(f => f.HasChanges && (f.Value !== '' || f.Note !== ''));
    
    if (changedFields.length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      let successCount = 0;
      let errorCount = 0;

      for (const field of changedFields) {
        try {
          const url = field.MonitorDataID
            ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data/${field.MonitorDataID}`
            : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data`;
          
          const method = field.MonitorDataID ? 'PUT' : 'POST';
          
          const payload = {
            BackstopID: parseInt(selectedFund),
            FieldID: field.FieldID,
            MonitorDate: formatDateForAPI(selectedDate),
            FieldValue: field.Value !== '' ? parseFloat(field.Value) : null,
            Note: field.Note || null
          };

          const response = await fetch(url, {
            method,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to save ${field.FieldName}`);
          }
        } catch (err) {
          errorCount++;
          console.error(`Error saving ${field.FieldName}:`, err);
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully saved ${successCount} field${successCount > 1 ? 's' : ''}`);
        await loadMonitorData();
      }
      
      if (errorCount > 0) {
        setError(`${errorCount} field${errorCount > 1 ? 's' : ''} failed to save`);
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const formatThresholdValue = (value: string) => {
    if (!value || value === '') return '-';
    const num = parseFloat(value);
    return isNaN(num) ? '-' : num.toFixed(2);
  };

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  const calculateAUMChangeByName = async (aumFieldId: number, currentAUM: number, changeFieldName: string, fieldTypeName: string) => {
    try {
      console.log('=== calculateAUMChangeByName DEBUG ===');
      console.log('Looking for change field:', changeFieldName);
      console.log('All field names:', fieldData.map(f => f.FieldName));
      
      const changeField = fieldData.find(f => f.FieldName === changeFieldName);
      console.log('Found change field:', changeField ? `Yes (ID: ${changeField.FieldID})` : 'NO');
      if (!changeField) {
        console.log('STOPPED: No change field found');
        return;
      }
      
      const monthlyRORField = fieldData.find(f => f.FieldName === 'Monthly ROR (%)');
      console.log('Found Monthly ROR field:', monthlyRORField ? 'Yes' : 'NO');
      if (!monthlyRORField) {
        console.log('STOPPED: No Monthly ROR field found');
        return;
      }
      
      console.log('Monthly ROR Value:', monthlyRORField.Value);
      if (!monthlyRORField.Value) {
        console.log('STOPPED: Monthly ROR has no value');
        return;
      }
      
      const monthlyROR = parseFloat(monthlyRORField.Value);
      console.log('Monthly ROR parsed:', monthlyROR);
      if (isNaN(monthlyROR)) {
        console.log('STOPPED: Monthly ROR is not a number');
        return;
      }
      
      console.log('All checks passed, calling performAUMCalculation');
      await performAUMCalculation(aumFieldId, currentAUM, changeField.FieldID, monthlyROR, fieldTypeName);
    } catch (err) {
      console.error(`Error in calculateAUMChangeByName:`, err);
    }
  };
  
  const performAUMCalculation = async (aumFieldId: number, currentAUM: number, changeFieldId: number, monthlyROR: number, fieldTypeName: string) => {
    try {
      console.log('=== AUM CALCULATION DEBUG ===');
      console.log('Input aumFieldId:', aumFieldId);
      console.log('Input currentAUM:', currentAUM);
      console.log('Input monthlyROR:', monthlyROR);
      console.log('Selected Date (original):', selectedDate);
      console.log('Selected Date ISO:', selectedDate ? new Date(selectedDate).toISOString() : 'null');
      console.log('Selected Date getMonth():', selectedDate ? selectedDate.getMonth() : 'null');
      console.log('Selected Date getFullYear():', selectedDate ? selectedDate.getFullYear() : 'null');
      
      if (!selectedDate) return;
      
      // Create a NEW date for previous month calculation
      const previousMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
      console.log('Previous month INITIAL (1st of prev month):', previousMonth.toISOString());
      console.log('Previous month getMonth():', previousMonth.getMonth());
      console.log('Previous month getFullYear():', previousMonth.getFullYear());
      // Set to last day of previous month
      const lastDayOfPrevMonth = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0).getDate();
      previousMonth.setDate(lastDayOfPrevMonth);
      const previousMonthStr = formatDateForAPI(previousMonth);
      console.log('Last day of previous month:', lastDayOfPrevMonth);
      console.log('Previous month FINAL:', previousMonth.toISOString());
      console.log('Previous month final date string for API:', previousMonthStr);
      
      const token = await getAccessToken();
      if (!token) return;
      
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data?backstop_id=${selectedFund}&start_date=${previousMonthStr}&end_date=${previousMonthStr}`;
      console.log('Fetching previous month from URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('API call failed:', response.status);
        return;
      }
      
      const data = await response.json();
      console.log('Previous month API response:', data);
      console.log('Looking for FieldID:', aumFieldId);
      const previousAUMData = data.find((d: any) => d.FieldID === aumFieldId);
      console.log('Found previous AUM data:', previousAUMData);
      
      if (!previousAUMData || previousAUMData.FieldValue === null) {
        console.log('No previous month data found for FieldID:', aumFieldId);
        return;
      }
      
      const previousAUM = parseFloat(previousAUMData.FieldValue);
      console.log('Previous AUM value:', previousAUM);
      if (isNaN(previousAUM) || previousAUM === 0) {
        console.log('Previous AUM is invalid');
        return;
      }
      
      const aumPctChange = 100 * (currentAUM / (previousAUM * (1 + monthlyROR / 100)) - 1);
      const aumPctChangeStr = aumPctChange.toFixed(2);
      
      // Create note with calculation details
      const calculationNote = `Auto-calc: ${currentAUM.toFixed(2)} / (${previousAUM.toFixed(2)} * (1+${monthlyROR.toFixed(2)}%/100)) - 1`;
      
      // Automatically update the field with the calculated value and note
      setFieldData(prev => prev.map(field => {
        if (field.FieldID === changeFieldId) {
          return { 
            ...field, 
            Value: aumPctChangeStr, 
            Note: calculationNote,
            HasChanges: true 
          };
        }
        return field;
      }));
      
    } catch (err) {
      console.error(`Error in performAUMCalculation:`, err);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Update Monitoring Data
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Fund Name</InputLabel>
              <Select
                value={selectedFund}
                onChange={(e) => setSelectedFund(e.target.value)}
                label="Fund Name"
              >
                {displayedFunds.map(fund => (
                  <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                    {fund.FundName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={investedOnly}
                  onChange={(e) => setInvestedOnly(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Invested funds only</Typography>}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onAccept={(newDate) => {
                  // Only update when user confirms selection (both month and year selected)
                  if (newDate) {
                    const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
                    setSelectedDate(lastDay);
                  } else {
                    setSelectedDate(newDate);
                  }
                }}
                views={['month', 'year']}
                format="MMM yy"
                disabled={!selectedFund}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'Dec 25',
                    helperText: monitorDates.length > 0 ? `Most recent: ${formatDateDisplay(monitorDates[0].MonitorDate)}` : undefined
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {selectedFund && (
          <>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Monitor Fields
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  disabled={!selectedDate}
                  size="small"
                >
                  Import
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || !selectedDate || fieldData.filter(f => f.HasChanges).length === 0}
                  sx={{ backgroundColor: '#1a237e' }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {!selectedDate ? 'Loading thresholds...' : 'Loading monitor data...'}
                </Typography>
              </Box>
            ) : fieldData.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No monitoring fields configured for this fund. Please set up thresholds first.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Monitor Field</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>L1</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>L2</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>U1</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>U2</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Note</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', width: 100 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', width: 120 }}>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fieldData.map((field) => (
                      <TableRow 
                        key={field.FieldID}
                        sx={{ 
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          backgroundColor: field.HasChanges ? '#fff3e0' : 'inherit'
                        }}
                      >
                        <TableCell>{field.FieldName}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatThresholdValue(field.L1)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatThresholdValue(field.L2)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatThresholdValue(field.U1)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatThresholdValue(field.U2)}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={field.Value}
                            onChange={(e) => handleValueChange(field.FieldID, e.target.value)}
                            onBlur={(e) => {
                              const value = e.target.value;
                              console.log('=== ONBLUR DEBUG ===');
                              console.log('Field blurred:', field.FieldName);
                              console.log('Value:', value);
                              console.log('isLoadingData:', isLoadingData);
                              
                              if (!isLoadingData && value && !isNaN(parseFloat(value))) {
                                const changedField = fieldData.find(f => f.FieldID === field.FieldID);
                                console.log('Changed field name:', changedField?.FieldName);
                                
                                if (changedField && (changedField.FieldName === 'AUM Fund ($mm)' || changedField.FieldName === 'AUM Fund ($mm) USD' || changedField.FieldName === 'AUM Fund ($mm) EUR')) {
                                  console.log('Matched AUM FUND field, triggering calculation');
                                  setFieldData(prev => {
                                    const monthlyRORField = prev.find(f => f.FieldName === 'Monthly ROR (%)');
                                    if (monthlyRORField?.Value) {
                                      calculateAUMChangeByName(field.FieldID, parseFloat(value), 'AUM Fund Change (%)', 'AUM Fund');
                                    }
                                    return prev;
                                  });
                                } else if (changedField && (changedField.FieldName === 'AUM Strategy ($mm)' || changedField.FieldName === 'AUM Strategy ($mm) USD' || changedField.FieldName === 'AUM Strategy ($mm) EUR')) {
                                  console.log('Matched AUM STRATEGY field, triggering calculation');
                                  setFieldData(prev => {
                                    const monthlyRORField = prev.find(f => f.FieldName === 'Monthly ROR (%)');
                                    if (monthlyRORField?.Value) {
                                      calculateAUMChangeByName(field.FieldID, parseFloat(value), 'AUM Strategy Change (%)', 'AUM Strategy');
                                    }
                                    return prev;
                                  });
                                } else {
                                  console.log('Field did not match any AUM field pattern');
                                }
                              } else {
                                console.log('Skipped: isLoadingData=', isLoadingData, 'hasValue=', !!value, 'isNumber=', value ? !isNaN(parseFloat(value)) : false);
                              }
                            }}
                            disabled={!selectedDate}
                            fullWidth
                            inputProps={{ step: '0.01' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={field.Note}
                            onChange={(e) => handleNoteChange(field.FieldID, e.target.value)}
                            disabled={!selectedDate}
                            fullWidth
                            multiline
                            maxRows={2}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          {field.UserName || '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          {field.Timestamp ? new Date(field.Timestamp).toLocaleString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {!selectedFund && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select a Fund to Begin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a fund from the dropdown above to view and update monitoring data
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DataEntryForm;
